import { getLocalJsonFileByPath } from '../../../../support/localFileRegistry';
import { setSettings } from '../../../../support/slices/tileBuilderSlice';
import { AppDispatch } from '../../../../support/store';
import {
  getMarginPresetKey,
  getStoredMarginsForContext,
} from '../../../../support/tileDisplayLayoutUtils';
import { TileDisplayContext } from '../../../../support/tileDisplayContextUtils';
import { TileRuntimeContext } from '../../../../support/tileRuntimeContext';
import { ensureNumber } from '../../../../support/utils';
import { Query, TileSettings } from '../../../../types/tileBuilderTypes';
import { ChartKind, LeaderLayoutConfig, MarginSettings } from './tilePreviewTypes';

export function calculateRealLeaderLayout(
  stripCount: number,
  containerHeight: number,
  maxPreviewWidth = 700
): LeaderLayoutConfig {
  const accordionPadding = 12;
  const gridGap = 12;
  const tileCardPadding = 8;
  const headerHeight = 20;
  const headerMarginBottom = 4;

  const availableGridHeight = containerHeight - accordionPadding * 2;
  const idealTotalWidth =
    stripCount === 1 ? maxPreviewWidth : Math.min(maxPreviewWidth, stripCount * 300);
  const availableGridWidth = idealTotalWidth - accordionPadding * 2;

  const totalGridGaps = Math.max(0, (stripCount - 1) * gridGap);
  const availableWidthForTiles = availableGridWidth - totalGridGaps;
  const tileTotalWidth = Math.floor(availableWidthForTiles / stripCount);

  const tileContentWidth = tileTotalWidth - tileCardPadding * 2;
  const tileContentHeight = availableGridHeight - tileCardPadding * 2;

  const chartAreaWidth = tileContentWidth;
  const chartAreaHeight = tileContentHeight - headerHeight - headerMarginBottom;

  const needsScaling = idealTotalWidth > maxPreviewWidth;
  const previewScale = needsScaling ? maxPreviewWidth / idealTotalWidth : 1;

  return {
    stripCount,
    containerHeight,
    containerWidth: idealTotalWidth,
    accordionPadding,
    gridGap,
    tileCardPadding,
    headerHeight,
    headerMarginBottom,
    chartAreaWidth: Math.max(chartAreaWidth, 200),
    chartAreaHeight: Math.max(chartAreaHeight, 160),
    previewScale,
    needsScaling,
  };
}

export function getChartKind(type: string | undefined): ChartKind {
  const t = (type || '').toLowerCase();
  if (t.includes('datagrid')) return 'datagrid';
  if (t.includes('metric')) return 'metric';
  if (t.includes('bar')) return 'bar';
  if (t.includes('line')) return 'line';
  if (t.includes('donut')) return 'donut';
  if (t.includes('gauge')) return 'gauge';
  if (t.includes('pie')) return 'pie';
  return 'unknown';
}

export function getLayoutStrategy(settings: TileSettings): 'auto' | 'manual' {
  return (settings as any).layoutStrategy === 'manual' ? 'manual' : 'auto';
}

export function getEffectiveMarginPreset(
  settings: TileSettings,
  displayContext: TileDisplayContext,
  previewDisplayContext?: 'dashboard' | 'leader'
) {
  return getMarginPresetKey(displayContext, !!settings.show_legend, previewDisplayContext);
}

export function getEffectiveMargins(
  settings: TileSettings,
  displayContext: TileDisplayContext,
  previewDisplayContext?: 'dashboard' | 'leader'
): MarginSettings {
  return getStoredMarginsForContext(settings as any, displayContext, {
    previewDisplayContext,
    showLegend: !!settings.show_legend,
  });
}

export function setEffectiveMargins(
  dispatch: AppDispatch,
  chartId: string,
  settings: TileSettings,
  next: MarginSettings,
  displayContext: TileDisplayContext,
  previewDisplayContext?: 'dashboard' | 'leader'
): void {
  const s = settings as any;
  const presetKey = getEffectiveMarginPreset(settings, displayContext, previewDisplayContext);

  const normalizedNext: MarginSettings = {
    top: ensureNumber(next.top, 24),
    right: ensureNumber(next.right, 16),
    bottom: ensureNumber(next.bottom, 48),
    left: ensureNumber(next.left, 16),
  };

  dispatch(
    setSettings({
      chartId,
      settings: {
        ...s,
        [presetKey]: normalizedNext,
        marginTop: normalizedNext.top,
        marginRight: normalizedNext.right,
        marginBottom: normalizedNext.bottom,
        marginLeft: normalizedNext.left,
      } as any,
    })
  );
}

export function substituteVariables(str: string, variables: Record<string, string>) {
  return str.replace(/\{(\w+)\}/g, (_, k) => variables[k] ?? `{${k}}`);
}

export function getEmailPrefix(value: string | undefined | null) {
  if (!value) return '';
  return String(value).split('@')[0].trim().toLowerCase();
}

export function resolveLocalFileData(
  fileData: any,
  rawPath: string,
  availableVariables: Record<string, string>
) {
  if (fileData == null) return undefined;

  if (!rawPath || !rawPath.trim()) return fileData;

  const substitutedPath = substituteVariables(rawPath, availableVariables).trim();
  if (!substitutedPath) return fileData;

  const arrayData = Array.isArray(fileData) ? fileData : [fileData];
  const lastSegment = substitutedPath.split('/').filter(Boolean).pop() || '';
  const emailPrefix = getEmailPrefix(lastSegment);

  if (!emailPrefix) return fileData;

  const matchedUser = arrayData.find((item: any) => {
    const empCecid = String(item?.EMP_CECID ?? '').toLowerCase();
    const empCecidDisp = String(item?.EMP_CECID_DISP ?? '').toLowerCase();
    const leaderCecid = String(item?.LDR_CECID ?? '').toLowerCase();

    return (
      empCecid === emailPrefix ||
      empCecidDisp === emailPrefix ||
      leaderCecid === emailPrefix
    );
  });

  if (!matchedUser) return fileData;
  if (Array.isArray(matchedUser?.reports)) return matchedUser.reports;
  return matchedUser;
}

export function getResolvedQueryResult(
  q: Query,
  runtimeContext: TileRuntimeContext,
  fallbackVariables: Record<string, string>
) {
  const availableVariables: Record<string, string> = {
    name: runtimeContext?.name ?? fallbackVariables.name ?? '',
    email:
      runtimeContext?.email ??
      runtimeContext?.userEmail ??
      runtimeContext?.ownerEmail ??
      fallbackVariables.email ??
      '',
    sfId: runtimeContext?.sfId ?? fallbackVariables.sfId ?? '',
    managerEmail: runtimeContext?.managerEmail ?? '',
    domain: (runtimeContext?.domain as string) ?? '',
    current_time_filter:
      (runtimeContext?.current_time_filter as string) ??
      (fallbackVariables as any)?.current_time_filter ??
      'all',
  };

  if ((q as any)?.dataSource === 'Local File') {
    const file = getLocalJsonFileByPath((q as any).localFilePath);
    return resolveLocalFileData(file?.data, q.path || '', availableVariables) ?? null;
  }

  return (q as any).functionResult ?? q.queryResult ?? null;
}